/**
 * 방문요양 월별 임금설정 — 급여대장 입력 필드 정의
 * (폼 변수명 ↔ DB: caion_wage_home_data.cwhd_*)
 *
 * | 폼 key | DB 컬럼 |
 * | m_code, yearMonth, payDate | m_code, cwhd_ym, cwhd_pay_date |
 * | payCalcHourly | cwhd_pay_calc_hourly |
 */

import { HOME_WAGE_SUPPORT_DEDUCTION_FIELD_TOOLTIP } from './homeWageSupportDeductionPolicy';

/** 비과세 수당 표준 명칭 — UI·툴팁·명세서·엑셀 공통 */
export const HOME_WAGE_NONTAX_TERM = {
  lunch: '중식비',
  lunchAuto: '중식비(자동)',
  lunchDirector: '중식비(센터장)',
  lunchCombined: '중식비 합계',
  lunchEligible: '중식비 가능',
  lunchExpense: '중식비 실비변상',
  car: '차량보조비',
  child: '양육비',
} as const;

export interface HomeWagePayrollFormData {
  name: string;
  birth: string;
  job: string;
  basic: number;
  weekend: number;
  annual: number;
  laborDay: number;
  overtime: number;
  night: number;
  holiday: number;
  /** 연·야·휴 비과세분 (DB: cwhd_*_nontax) */
  overtimeNontax: number;
  nightNontax: number;
  holidayNontax: number;
  senior: number;
  sunim: number;
  bosu: number;
  severe: number;
  lunch: number;
  /** 센터장 입력 중식비 (cwhd_lunch_director) */
  lunchDirector: number;
  bonus: number;
  car: number;
  child: number;
  welfare: number;
  other: number;
  wageTotal: number;
  l_yeon: number;
  l_gun: number;
  l_jang: number;
  l_go: number;
  l_so: number;
  l_ji: number;
  l_jiwon: number;
  l_bonin: number;
  l_etc: number;
  l_total: number;
  c_yeon: number;
  c_gun: number;
  c_jang: number;
  c_go: number;
  c_sil: number;
  c_san: number;
  c_out: number;
  c_total: number;
  grossPay: number;
  net: number;
  nonTax: number;
  l_save: number;
  c_save: number;
  profit: number;
  svc: string;
  cnt: string;
  hour: string;
  days: string;
  memo: string;
  /** 연장·야간·휴일 시간(시) — UI·계산용 (DB 컬럼 추가 전) */
  overtimeHours: number;
  nightHours: number;
  holidayHours: number;
}

export type HomeWagePayrollFieldKey = keyof HomeWagePayrollFormData;

export type HomeWagePremiumHoursKey = 'overtimeHours' | 'nightHours' | 'holidayHours';

export type HomeWagePremiumAmountKey = 'overtime' | 'night' | 'holiday';

export interface HomeWagePayrollFieldDef {
  key: HomeWagePayrollFieldKey;
  label: string;
  /** 금액 입력(콤마 포맷) */
  money?: boolean;
  /** 지급계산시급 변경 시 금액 자동 재계산 */
  hourlyLinked?: boolean;
  /** 시간 입력 → 금액 자동 계산 (연장·야간·휴일) */
  premiumHours?: boolean;
  hoursKey?: HomeWagePremiumHoursKey;
  /** 산출 방식 툴팁 */
  tooltip?: string;
  /** 카드 합계 행 강조 */
  isTotal?: boolean;
  /** 금액 표시만 (계산 결과) */
  readonly?: boolean;
}

/** 카드 합계 금액 필드 */
export const HOME_WAGE_PAYROLL_TOTAL_KEYS: HomeWagePayrollFieldKey[] = [
  'wageTotal',
  'nonTax',
  'l_total',
  'c_total',
];

export function isHomeWagePayrollTotalKey(key: HomeWagePayrollFieldKey): boolean {
  return HOME_WAGE_PAYROLL_TOTAL_KEYS.includes(key);
}

export interface HomeWagePayrollSectionDef {
  id: string;
  title: string;
  fields: HomeWagePayrollFieldDef[];
}

export function createEmptyPayrollForm(): HomeWagePayrollFormData {
  return {
    name: '',
    birth: '',
    job: '',
    basic: 0,
    weekend: 0,
    annual: 0,
    laborDay: 0,
    overtime: 0,
    night: 0,
    holiday: 0,
    overtimeNontax: 0,
    nightNontax: 0,
    holidayNontax: 0,
    senior: 0,
    sunim: 0,
    bosu: 0,
    severe: 0,
    lunch: 0,
    lunchDirector: 0,
    bonus: 0,
    car: 0,
    child: 0,
    welfare: 0,
    other: 0,
    wageTotal: 0,
    l_yeon: 0,
    l_gun: 0,
    l_jang: 0,
    l_go: 0,
    l_so: 0,
    l_ji: 0,
    l_jiwon: 0,
    l_bonin: 0,
    l_etc: 0,
    l_total: 0,
    c_yeon: 0,
    c_gun: 0,
    c_jang: 0,
    c_go: 0,
    c_sil: 0,
    c_san: 0,
    c_out: 0,
    c_total: 0,
    grossPay: 0,
    net: 0,
    nonTax: 0,
    l_save: 0,
    c_save: 0,
    profit: 0,
    svc: '',
    cnt: '',
    hour: '',
    days: '',
    memo: '',
    overtimeHours: 0,
    nightHours: 0,
    holidayHours: 0,
  };
}

/** 4대보험 고정(체크) — 계산 시 수동값 유지, 고정 시 입력 클릭 → 모달 */
export const HOME_WAGE_INSURANCE_FIX_CHECKBOX_TOOLTIP = [
  '체크 시 「계산」(지난달 데이터 가져오기 포함) 시',
  '해당 금액은 자동계산으로 덮어쓰지 않습니다.',
  '입력란 클릭 → 금액 설정 모달',
].join('\n');

/** 퇴직금(c_out) 필드 툴팁 */
export const HOME_WAGE_RETIREMENT_FIELD_TOOLTIP = [
  '총 지급금액 ÷ 12',
  '중식비 실비변상 체크 시: (총 지급금액 − 중식비) ÷ 12',
  '미체크 시 「계산」 버튼으로 자동 산출(60h·대표자 등 조건 적용)',
].join('\n');

/** 급여대장 섹션·필드 (표시 순서) — 기본정보(name/birth/job)는 툴바·DB용, 화면 미표시 */
export const HOME_WAGE_PAYROLL_SECTIONS: HomeWagePayrollSectionDef[] = [
  {
    id: 'wage',
    title: '임금구성',
    fields: [
      { key: 'basic', label: '기본급', money: true, tooltip: '지급계산시급 × 총수가시간' },
      { key: 'weekend', label: '주휴수당', money: true, tooltip: '기본급 × 0.2 (20%)' },
      { key: 'annual', label: '연차수당', money: true, tooltip: '(지급계산시급 × 총수가시간 ÷ 근무일수) × 연차일수 ÷ 12 + 차액' },
      { key: 'laborDay', label: '노동절수당', money: true, tooltip: '5월·60h·요양보호사 — 비과세 (자동 산출)' },
      { key: 'overtime', label: '연장수당', money: true, tooltip: '지급계산시급 × 1.5 × 연장시간' },
      { key: 'night', label: '야간수당', money: true, tooltip: '지급계산시급 × 0.5 × 야간시간 (22:00~06:00)' },
      { key: 'holiday', label: '휴일수당', money: true, tooltip: '지급계산시급 × 1.5 × 휴일시간' },
      { key: 'senior', label: '장기근속수당', money: true, tooltip: '근속연수에 따른 장기근속수당 (공단 지원)' },
      { key: 'bosu', label: '보수교육비', money: true, tooltip: '보수교육 수당' },
      { key: 'severe', label: '중증수급자가산', money: true, tooltip: '중증수급자 서비스 가산금 (서비스횟수 × 수가)' },
      { key: 'lunch', label: HOME_WAGE_NONTAX_TERM.lunchAuto, money: true, tooltip: '연차수당 잉여에서 산출 — min(점심근무일×1일 중식비, 남은금액, 20만원), 5천원 미만 시 0원' },
      {
        key: 'lunchDirector',
        label: HOME_WAGE_NONTAX_TERM.lunchDirector,
        money: true,
        readonly: true,
        tooltip: `센터장이 입력하는 ${HOME_WAGE_NONTAX_TERM.lunch} (월 최대 20만원, 저장 시 자동 산출분과 합산)`,
      },
      { key: 'bonus', label: '상여금', money: true, tooltip: '성과금/상여금' },
      { key: 'car', label: HOME_WAGE_NONTAX_TERM.car, money: true, tooltip: '자가운전 보조금 (비과세 월 20만원 한도)' },
      { key: 'child', label: HOME_WAGE_NONTAX_TERM.child, money: true, tooltip: '자녀 양육비 지원 (비과세 월 20만원 한도)' },
      { key: 'welfare', label: '처우개선비', money: true, tooltip: '처우개선 수당' },
      { key: 'other', label: '기타수당', money: true, tooltip: '기타 수당' },
      { key: 'wageTotal', label: '임금구성합계', money: true, tooltip: '모든 임금 항목의 합계 = 수급자별 합계금액' },
    ],
  },
  {
    id: 'laborDeduct',
    title: '근로자공제',
    fields: [
      { key: 'l_yeon', label: '국민연금', money: true, tooltip: '과세임금 × 국민연금요율 (4.5%)' },
      { key: 'l_gun', label: '건강보험', money: true, tooltip: '과세임금 × 건강보험요율' },
      { key: 'l_jang', label: '장기요양보험', money: true, tooltip: '건강보험료 × 장기요양보험요율' },
      { key: 'l_go', label: '고용보험', money: true, tooltip: '과세임금 × 고용보험요율 (0.9%)' },
      { key: 'l_so', label: '소득세', money: true, tooltip: '간이세액표 기준 소득세' },
      { key: 'l_ji', label: '지방세', money: true, tooltip: '소득세 × 10%' },
      { key: 'l_jiwon', label: '지원공제', money: true, tooltip: HOME_WAGE_SUPPORT_DEDUCTION_FIELD_TOOLTIP },
      { key: 'l_bonin', label: '본인부담금공제', money: true, tooltip: '본인부담금 공제액' },
      { key: 'l_etc', label: '기타공제', money: true, tooltip: '기타 근로자 공제액' },
      { key: 'l_total', label: '근로자공제합계', money: true, tooltip: '근로자 공제 항목 합계' },
    ],
  },
  {
    id: 'corpDeduct',
    title: '기관공제',
    fields: [
      { key: 'c_yeon', label: '국민연금', money: true, tooltip: '과세임금 × 국민연금요율 (4.5%)' },
      { key: 'c_gun', label: '건강보험', money: true, tooltip: '과세임금 × 건강보험요율' },
      { key: 'c_jang', label: '장기요양보험', money: true, tooltip: '건강보험료 × 장기요양보험요율' },
      { key: 'c_go', label: '고용보험', money: true, tooltip: '과세임금 × 고용보험요율 (사업주 부담)' },
      { key: 'c_sil', label: '실업급여', money: true, tooltip: '과세임금 × 실업급여요율' },
      { key: 'c_san', label: '산재보험', money: true, tooltip: '과세임금 × 산재보험요율 (사업장별 상이)' },
      { key: 'c_out', label: '퇴직금', money: true, tooltip: HOME_WAGE_RETIREMENT_FIELD_TOOLTIP },
      { key: 'c_total', label: '기관공제합계', money: true, tooltip: '기관 공제 항목 합계' },
    ],
  },
  {
    id: 'ref',
    title: '참고사항',
    fields: [
      { key: 'grossPay', label: '지급금액', money: true, tooltip: '과세임금 + 비과세액 = 총 지급액' },
      { key: 'net', label: '실수령액', money: true, tooltip: '지급금액 - 근로자공제합계' },
      { key: 'c_save', label: '인건비절감액', money: true, tooltip: `비과세합계(${HOME_WAGE_NONTAX_TERM.car}·${HOME_WAGE_NONTAX_TERM.child} 제외) 기준 — 4대보험·실업·산재(사복+퇴직) 절감액` },
      { key: 'profit', label: '기관수익', money: true, tooltip: '청구 수가금액 합계 - (지급금액 + 기관공제합계)' },
    ],
  },
  {
    id: 'work',
    title: '근무내역',
    fields: [
      { key: 'svc', label: '서비스명', tooltip: '제공한 서비스 유형' },
      { key: 'cnt', label: '횟수', tooltip: '서비스 제공 횟수' },
      { key: 'hour', label: '시간', tooltip: '총 근무 시간' },
      { key: 'days', label: '근로일', tooltip: '근무 일수' },
      { key: 'memo', label: '비고', tooltip: '특이사항' },
    ],
  },
];

/** 섹션별 자동채움 키 (수급자별 시급·수가시간 카드는 해당 없음) */
export type HomeWagePayrollAutofillKey =
  | 'payCalc'
  | 'taxable'
  | 'nonTax'
  | 'socialInsurance'
  | 'laborDeduct'
  | 'corpDeduct';

export interface HomeWageInsurancePairDef {
  key: string;
  label: string;
  laborKey: HomeWagePayrollFieldKey;
  corpKey: HomeWagePayrollFieldKey;
  tooltip?: string;
}

/** 4대보험 — 국민·건강·장기는 입력 1개(근로·기관 동일 금액), 고용은 근로·기관 분리 입력 */
export const HOME_WAGE_INSURANCE_PAIRS: HomeWageInsurancePairDef[] = [
  { key: 'yeon', label: '국민연금', laborKey: 'l_yeon', corpKey: 'c_yeon', tooltip: '과세임금 × 4.5% (근로자·기관 각각)' },
  { key: 'gun', label: '건강보험', laborKey: 'l_gun', corpKey: 'c_gun', tooltip: '과세임금 × 건강보험요율 (근로자·기관 각각)' },
  { key: 'jang', label: '장기요양보험', laborKey: 'l_jang', corpKey: 'c_jang', tooltip: '건강보험료 × 장기요양요율 (근로자·기관 각각)' },
  { key: 'go', label: '고용보험', laborKey: 'l_go', corpKey: 'c_go', tooltip: '과세임금 × 0.9% (근로자·기관 각각)' },
];

/** 4대보험 중 공유 입력(국민·건강·장기) */
export const HOME_WAGE_INSURANCE_SHARED_PAIRS = HOME_WAGE_INSURANCE_PAIRS.filter((p) => p.key !== 'go');

/** 고용보험(근로·기관 분리) */
export const HOME_WAGE_EMPLOYMENT_INSURANCE_PAIR = HOME_WAGE_INSURANCE_PAIRS.find((p) => p.key === 'go')!;

export interface HomeWagePayrollCardDef {
  id: string;
  title: string;
  /** 카드 부제 (자동채움 범위 안내) */
  hint?: string;
  fields?: HomeWagePayrollFieldDef[];
  /** 4대보험 쌍 레이아웃 */
  insurancePairs?: boolean;
  autofillKey?: HomeWagePayrollAutofillKey;
}

/** 급여대장 카드 (메인 콘텐츠 표시 순서) */
export const HOME_WAGE_PAYROLL_CARDS: HomeWagePayrollCardDef[] = [
  {
    id: 'taxable',
    title: '과세 임금',
    autofillKey: 'taxable',
    fields: [
      { key: 'basic', label: '기본급', money: true, hourlyLinked: true, tooltip: '지급계산시급 × 총수가시간' },
      { key: 'weekend', label: '주휴수당', money: true, hourlyLinked: true, tooltip: '기본급 × 0.2 (20%)' },
      { key: 'annual', label: '연차수당', money: true, hourlyLinked: true, tooltip: '(지급계산시급 × 총수가시간 ÷ 근무일수) × 연차일수 ÷ 12 + 차액' },
      { key: 'severe', label: '중증수급자가산', money: true, hourlyLinked: true, tooltip: '중증수급자 서비스 가산금 (서비스횟수 × 수가)' },
      { key: 'senior', label: '장기근속수당', money: true, tooltip: '근속연수에 따른 장기근속수당 (공단 지원)' },
      { key: 'bosu', label: '보수교육비', money: true, tooltip: '보수교육 수당' },
      { key: 'bonus', label: '상여금', money: true, tooltip: '성과금/상여금' },
      { key: 'welfare', label: '처우개선비', money: true, tooltip: '처우개선 수당' },
      { key: 'other', label: '기타수당', money: true, tooltip: '기타 수당' },
      { key: 'wageTotal', label: '과세액 합계', money: true, isTotal: true, tooltip: '모든 과세 임금 항목의 합계' },
    ],
  },
  {
    id: 'nonTax',
    title: '비과세 수당',
    autofillKey: 'nonTax',
    fields: [
      {
        key: 'laborDay',
        label: '노동절수당',
        money: true,
        readonly: true,
        tooltip: '5월·60h·요양보호사 — 비과세 (자동 산출, 필드 툴팁에 조건·산식·250% 상세)',
      },
      { key: 'lunch', label: HOME_WAGE_NONTAX_TERM.lunchAuto, money: true, tooltip: '연차수당 잉여에서 산출 — min(점심근무일×1일 중식비, 남은금액, 20만원), 5천원 미만 시 0원' },
      {
        key: 'lunchDirector',
        label: HOME_WAGE_NONTAX_TERM.lunchDirector,
        money: true,
        readonly: true,
        tooltip: `센터장이 입력하는 ${HOME_WAGE_NONTAX_TERM.lunch} (월 최대 20만원, 저장 시 자동 산출분과 합산)`,
      },
      {
        key: 'overtime',
        label: '연장수당',
        money: true,
        readonly: true,
        premiumHours: true,
        hoursKey: 'overtimeHours',
        tooltip: '시급 × 0.5 × 연장시간 (일 8h 초과, B1/B2 plan 겹침 병합)',
      },
      {
        key: 'night',
        label: '야간수당',
        money: true,
        readonly: true,
        premiumHours: true,
        hoursKey: 'nightHours',
        tooltip: '시급 × 0.5 × 야간시간 (22:00~06:00)',
      },
      {
        key: 'holiday',
        label: '휴일수당',
        money: true,
        readonly: true,
        premiumHours: true,
        hoursKey: 'holidayHours',
        tooltip: '시급 × 0.5 × 휴일시간 (일요일가산·공휴일가산 수가)',
      },
      { key: 'car', label: HOME_WAGE_NONTAX_TERM.car, money: true, tooltip: '자가운전 보조금 (비과세 월 20만원 한도)' },
      { key: 'child', label: HOME_WAGE_NONTAX_TERM.child, money: true, tooltip: '자녀 양육비 지원 (비과세 월 20만원 한도)' },
      { key: 'nonTax', label: '비과세액 합계', money: true, isTotal: true, tooltip: `${HOME_WAGE_NONTAX_TERM.lunch} + 노동절 + ${HOME_WAGE_NONTAX_TERM.car} + ${HOME_WAGE_NONTAX_TERM.child} + (생산직) 연·야·휴 비과세분` },
    ],
  },
  {
    id: 'socialInsurance',
    title: '4대보험',
    insurancePairs: true,
    autofillKey: 'socialInsurance',
  },
  {
    id: 'laborDeduct',
    title: '근로자공제',
    autofillKey: 'laborDeduct',
    fields: [
      { key: 'l_so', label: '소득세', money: true, tooltip: '간이세액표 기준 소득세' },
      { key: 'l_ji', label: '지방세', money: true, tooltip: '소득세 × 10%' },
      { key: 'l_jiwon', label: '지원공제', money: true, tooltip: HOME_WAGE_SUPPORT_DEDUCTION_FIELD_TOOLTIP },
      { key: 'l_bonin', label: '본인부담금공제', money: true, tooltip: '본인부담금 공제액' },
      { key: 'l_etc', label: '기타공제', money: true, tooltip: '기타 근로자 공제액' },
      { key: 'l_total', label: '근로자공제합계', money: true, isTotal: true, tooltip: '4대보험(근로자) + 소득세 + 지방세 + 기타공제' },
    ],
  },
  {
    id: 'corpDeduct',
    title: '기관공제',
    autofillKey: 'corpDeduct',
    fields: [
      { key: 'c_sil', label: '실업급여', money: true, tooltip: '과세임금 × 실업급여요율' },
      { key: 'c_san', label: '산재보험', money: true, tooltip: '과세임금 × 산재보험요율 (사업장별 상이)' },
      { key: 'c_out', label: '퇴직금', money: true, tooltip: HOME_WAGE_RETIREMENT_FIELD_TOOLTIP },
      { key: 'c_total', label: '기관공제합계', money: true, isTotal: true, tooltip: '4대보험(기관) + 실업급여 + 산재 + 퇴직금' },
    ],
  },
  {
    id: 'reference',
    title: '참고사항',
    fields: [
      { key: 'grossPay', label: '지급금액', money: true, tooltip: '과세임금 + 비과세액 = 총 지급액' },
      { key: 'net', label: '실수령액', money: true, tooltip: '지급금액 - 근로자공제합계' },
      { key: 'c_save', label: '인건비절감액', money: true, tooltip: `비과세합계(${HOME_WAGE_NONTAX_TERM.car}·${HOME_WAGE_NONTAX_TERM.child} 제외) 기준 — 4대보험·실업·산재(사복+퇴직) 절감액` },
      { key: 'profit', label: '기관수익', money: true, tooltip: '청구 수가금액 합계 - (지급금액 + 기관공제합계)' },
    ],
  },
];

const taxableCardFields =
  HOME_WAGE_PAYROLL_CARDS.find((c) => c.id === 'taxable')?.fields ?? [];

/** 요양보호사 — 기본급 카드: 기본급·주휴·연차·중증수급자가산 (hourlyLinked만) */
export const HOME_WAGE_BASE_PAY_FIELDS: HomeWagePayrollFieldDef[] = taxableCardFields.filter(
  (f) => f.hourlyLinked
);

/** 요양보호사 — 기본급 카드 합계용 키 목록 */
export const HOME_WAGE_BASE_PAY_KEYS: HomeWagePayrollFieldKey[] = ['basic', 'weekend', 'annual', 'severe'];

/** 요양보호사 — 연·야·휴 (과세/비과세 분리 표시) */
export const HOME_WAGE_CAREGIVER_PREMIUM_FIELDS: HomeWagePayrollFieldDef[] = (
  HOME_WAGE_PAYROLL_CARDS.find((c) => c.id === 'nonTax')?.fields ?? []
).filter((f) => f.premiumHours);

/** 비과세 수당 카드 — 중식비·차량보조비·양육비 (연·야·휴 제외) */
export const HOME_WAGE_NON_TAX_CASH_FIELDS: HomeWagePayrollFieldDef[] = (
  HOME_WAGE_PAYROLL_CARDS.find((c) => c.id === 'nonTax')?.fields ?? []
).filter(
  (f) => !f.premiumHours && !f.isTotal && f.key !== 'nonTax' && f.key !== 'laborDay'
);

/** 센터장 시점 — 자동 중식대 (readonly) */
export const HOME_WAGE_LUNCH_AUTO_FIELD: HomeWagePayrollFieldDef = {
  key: 'lunch',
  label: HOME_WAGE_NONTAX_TERM.lunchAuto,
  money: true,
  readonly: true,
  tooltip:
    '연차수당 잉여에서 산출 — min(점심근무일×1일 중식비, 남은금액, 20만원), 5천원 미만 시 0원',
};

/** 센터장 시점 — 중식비 입력 */
export const HOME_WAGE_LUNCH_DIRECTOR_FIELD: HomeWagePayrollFieldDef = {
  key: 'lunchDirector',
  label: HOME_WAGE_NONTAX_TERM.lunch,
  money: true,
  tooltip: `센터장이 입력하는 ${HOME_WAGE_NONTAX_TERM.lunch} (월 최대 20만원)`,
};

/** 노동절수당 — 비과세 카드 (5월·요양보호사, 자동 산출) */
export const HOME_WAGE_LABOR_DAY_NONTAX_FIELD: HomeWagePayrollFieldDef | undefined = (
  HOME_WAGE_PAYROLL_CARDS.find((c) => c.id === 'nonTax')?.fields ?? []
).find((f) => f.key === 'laborDay');

/** 비요양보호사 — 근무정보 카드: 기본급 + 연장·야간·휴일 (과세) */
export const HOME_WAGE_STAFF_WORK_INFO_FIELDS: HomeWagePayrollFieldDef[] = [
  { key: 'basic', label: '기본급', money: true, tooltip: '시급 × 근무시간 (계산 버튼) 또는 직접 입력' },
  {
    key: 'overtime',
    label: '연장수당',
    money: true,
    readonly: true,
    premiumHours: true,
    hoursKey: 'overtimeHours',
    tooltip: '시급 × 1.5 × 연장시간',
  },
  {
    key: 'night',
    label: '야간수당',
    money: true,
    readonly: true,
    premiumHours: true,
    hoursKey: 'nightHours',
    tooltip: '시급 × 0.5 × 야간시간 (22:00~06:00)',
  },
  {
    key: 'holiday',
    label: '휴일수당',
    money: true,
    readonly: true,
    premiumHours: true,
    hoursKey: 'holidayHours',
    tooltip: '시급 × 1.5 × 휴일시간',
  },
];

/** 비요양보호사 — 근무정보 카드 합계용 키 목록 */
export const HOME_WAGE_STAFF_WORK_INFO_KEYS: HomeWagePayrollFieldKey[] = [
  'basic', 'overtime', 'night', 'holiday',
];

/** 비요양보호사 — 과세 수당: 연차 + 기타 과세 (연장·야간·휴일 제외) */
export const HOME_WAGE_STAFF_TAXABLE_OTHER_FIELDS: HomeWagePayrollFieldDef[] = [
  { key: 'annual', label: '연차수당', money: true, tooltip: '연차수당 (수동 입력)' },
  { key: 'senior', label: '장기근속수당', money: true, tooltip: '근속연수에 따른 장기근속수당 (공단 지원)' },
  { key: 'sunim', label: '선임수당', money: true, tooltip: '선임(수임) 수당 (공단 지원)' },
  ...taxableCardFields.filter(
    (f) => !f.hourlyLinked && !f.premiumHours && f.key !== 'wageTotal' && f.key !== 'senior'
  ),
];

/** 비요양보호사 — 과세 수당 합계용 키 목록 */
export const HOME_WAGE_STAFF_TAXABLE_OTHER_KEYS: HomeWagePayrollFieldKey[] = [
  'annual', 'senior', 'sunim', 'bosu', 'bonus', 'welfare', 'other',
];

/** 요양보호사 — 과세 수당: 기타 과세 (연·야·휴는 비과세 카드) */
export const HOME_WAGE_TAXABLE_OTHER_FIELDS: HomeWagePayrollFieldDef[] = taxableCardFields.filter(
  (f) => !f.hourlyLinked && f.key !== 'wageTotal'
);

/** 요양보호사 — 과세 수당 합계용 키 목록 */
export const HOME_WAGE_TAXABLE_OTHER_KEYS: HomeWagePayrollFieldKey[] = [
  'senior', 'bosu', 'bonus', 'welfare', 'other',
];

/**
 * 지난달 데이터 가져오기 — 근무일·근무시간과 무관하게 전월 금액을 그대로 복사하는 수당
 * (요양보호사·사복·센터장 공통)
 *
 * | key | 항목 |
 * |-----|------|
 * | senior | 장기근속수당 |
 * | bosu | 보수교육비 |
 * | bonus | 상여금 |
 * | welfare | 처우개선비 |
 * | other | 기타수당 |
 * | lunchDirector | 중식비(센터장 입력) |
 * | car | 차량보조비 |
 * | child | 양육비 |
 */
export const HOME_WAGE_PREV_MONTH_FIXED_COPY_KEYS: HomeWagePayrollFieldKey[] = [
  'senior',
  'bosu',
  'bonus',
  'welfare',
  'other',
  'lunchDirector',
  'car',
  'child',
];

/** 전월 고정복사 — 과세 수당만 */
export const HOME_WAGE_PREV_MONTH_FIXED_COPY_TAXABLE_KEYS: HomeWagePayrollFieldKey[] = [
  'senior',
  'bosu',
  'bonus',
  'welfare',
  'other',
];

/** 전월 고정복사 — 비과세(센터장식대·차량·자녀) */
export const HOME_WAGE_PREV_MONTH_FIXED_COPY_NONTAX_KEYS: HomeWagePayrollFieldKey[] = [
  'lunchDirector',
  'car',
  'child',
];

/** 비요양보호사 — 센터장 시점 통합 수당 (2열 그리드, 연·야·휴는 근무정보 카드) */
export const HOME_WAGE_STAFF_DIRECTOR_MERGED_ALLOWANCE_FIELDS: HomeWagePayrollFieldDef[] = [
  ...HOME_WAGE_STAFF_TAXABLE_OTHER_FIELDS,
  HOME_WAGE_LUNCH_DIRECTOR_FIELD,
  ...HOME_WAGE_NON_TAX_CASH_FIELDS.filter((f) => f.key === 'car' || f.key === 'child'),
];

/** 비요양보호사 — 센터장 통합 수당 합계용 키 */
export const HOME_WAGE_STAFF_DIRECTOR_MERGED_ALLOWANCE_KEYS: HomeWagePayrollFieldKey[] = [
  ...HOME_WAGE_STAFF_TAXABLE_OTHER_KEYS,
  'lunchDirector',
  'car',
  'child',
];

/**
 * 요양보호사 — 센터장 시점 통합 수당 (2열 그리드)
 * 연·야·휴는 그리드에 넣지 않음 — 청구 시간 있을 때만 하단 지급/미지급 스위치로 표시
 * 고정 순서: 기타과세 → 중식비 → 차량보조비 → 양육비 (+ 노동절은 5월 computed에서 앞에 추가)
 */
export const HOME_WAGE_DIRECTOR_MERGED_ALLOWANCE_FIELDS: HomeWagePayrollFieldDef[] = [
  ...HOME_WAGE_TAXABLE_OTHER_FIELDS,
  HOME_WAGE_LUNCH_DIRECTOR_FIELD,
  ...HOME_WAGE_NON_TAX_CASH_FIELDS.filter((f) => f.key === 'car'),
  ...HOME_WAGE_NON_TAX_CASH_FIELDS.filter((f) => f.key === 'child'),
];

/** @deprecated HOME_WAGE_DIRECTOR_MERGED_ALLOWANCE_FIELDS 사용 */
export const HOME_WAGE_DIRECTOR_MERGED_CASH_FIELDS: HomeWagePayrollFieldDef[] =
  HOME_WAGE_DIRECTOR_MERGED_ALLOWANCE_FIELDS.filter((f) => !f.premiumHours);

/** 센터장 시점 통합 수당 합계용 키 (연·야·휴는 별도 full 합산) */
export const HOME_WAGE_DIRECTOR_MERGED_ALLOWANCE_KEYS: HomeWagePayrollFieldKey[] = [
  ...HOME_WAGE_TAXABLE_OTHER_KEYS,
  'lunchDirector',
  'car',
  'child',
];

/** 4대보험 과세임금 산정 — 과세 임금 카드 항목 (연·야·휴·노동절·합계·비과세 제외) */
export const HOME_WAGE_TAXABLE_COMPONENT_KEYS: HomeWagePayrollFieldKey[] = [
  'basic',
  'weekend',
  'annual',
  'senior',
  'sunim',
  'bosu',
  'severe',
  'bonus',
  'welfare',
  'other',
];

/** 센터장/사복 — 근무정보 연·야·휴 포함 과세 합계 */
export const HOME_WAGE_STAFF_TAXABLE_COMPONENT_KEYS: HomeWagePayrollFieldKey[] = [
  ...HOME_WAGE_TAXABLE_COMPONENT_KEYS,
  'overtime',
  'night',
  'holiday',
];

export const HOME_WAGE_TAXABLE_TOTAL_FIELD: HomeWagePayrollFieldDef | undefined = taxableCardFields.find(
  (f) => f.key === 'wageTotal'
);

/** 하위 호환: 기존 이름 유지 */
export const HOME_WAGE_TAXABLE_HOURLY_FIELDS = HOME_WAGE_BASE_PAY_FIELDS;

export const HOME_WAGE_REFERENCE_CARD: HomeWagePayrollCardDef =
  HOME_WAGE_PAYROLL_CARDS.find((c) => c.id === 'reference')!;

export const HOME_WAGE_NON_TAX_CARD: HomeWagePayrollCardDef =
  HOME_WAGE_PAYROLL_CARDS.find((c) => c.id === 'nonTax')!;

export const HOME_WAGE_SOCIAL_INSURANCE_CARD: HomeWagePayrollCardDef =
  HOME_WAGE_PAYROLL_CARDS.find((c) => c.id === 'socialInsurance')!;

export const HOME_WAGE_LABOR_DEDUCT_CARD: HomeWagePayrollCardDef =
  HOME_WAGE_PAYROLL_CARDS.find((c) => c.id === 'laborDeduct')!;

export const HOME_WAGE_CORP_DEDUCT_CARD: HomeWagePayrollCardDef =
  HOME_WAGE_PAYROLL_CARDS.find((c) => c.id === 'corpDeduct')!;

export const HOME_WAGE_TAXABLE_CARD: HomeWagePayrollCardDef =
  HOME_WAGE_PAYROLL_CARDS.find((c) => c.id === 'taxable')!;

/** 근로자공제 — 4대보험 외 항목 */
export const HOME_WAGE_LABOR_OTHER_DEDUCT_FIELDS: HomeWagePayrollFieldDef[] =
  HOME_WAGE_LABOR_DEDUCT_CARD.fields?.filter((f) => !f.isTotal && !isHomeWagePayrollTotalKey(f.key)) ?? [];

/** 본인부담금·기타 — 수동 입력 전용 (고정 체크박스 없음) */
export const HOME_WAGE_LABOR_MANUAL_DEDUCT_FIELD_KEYS = ['l_bonin', 'l_etc'] as const;

export const HOME_WAGE_LABOR_MANUAL_DEDUCT_FIELDS: HomeWagePayrollFieldDef[] =
  HOME_WAGE_LABOR_OTHER_DEDUCT_FIELDS.filter((f) =>
    (HOME_WAGE_LABOR_MANUAL_DEDUCT_FIELD_KEYS as readonly string[]).includes(f.key)
  );

/** 소득세·지방세·지원공제 — 고정(체크) 가능 */
export const HOME_WAGE_LABOR_FIX_DEDUCT_FIELDS: HomeWagePayrollFieldDef[] =
  HOME_WAGE_LABOR_OTHER_DEDUCT_FIELDS.filter(
    (f) => !(HOME_WAGE_LABOR_MANUAL_DEDUCT_FIELD_KEYS as readonly string[]).includes(f.key)
  );

export const HOME_WAGE_LABOR_TOTAL_FIELD: HomeWagePayrollFieldDef | undefined =
  HOME_WAGE_LABOR_DEDUCT_CARD.fields?.find((f) => f.key === 'l_total');

/** 기관공제 — 4대보험 외 항목 */
export const HOME_WAGE_CORP_OTHER_DEDUCT_FIELDS: HomeWagePayrollFieldDef[] =
  HOME_WAGE_CORP_DEDUCT_CARD.fields?.filter((f) => !f.isTotal && !isHomeWagePayrollTotalKey(f.key)) ?? [];

export const HOME_WAGE_CORP_TOTAL_FIELD: HomeWagePayrollFieldDef | undefined =
  HOME_WAGE_CORP_DEDUCT_CARD.fields?.find((f) => f.key === 'c_total');

/** 센터장/사회복지사 필드 정의 (별도 타입) */
export interface HomeWageStaffFieldDef {
  key: string;
  label: string;
  money?: boolean;
  hours?: boolean;
  tooltip?: string;
  isTotal?: boolean;
}

/**
 * 센터장/사회복지사 — 「급여 정보」 입력 필드
 * 레거시 정책: 시급, 근무시간(기본 209), 급여(계산)
 */
export const HOME_WAGE_WORK_FIELDS: HomeWageStaffFieldDef[] = [
  { key: 'hourlyWage', label: '시급', money: true, tooltip: '시간당 임금 (기본: 최저시급)' },
  { key: 'workHours', label: '근무시간', tooltip: '월 총 근무 시간 (기본: 209시간)' },
];

/** 센터장/사회복지사 — 급여 계산 결과 필드 */
export const HOME_WAGE_STAFF_CALC_FIELDS: HomeWageStaffFieldDef[] = [
  { key: 'basePay', label: '급여', money: true, tooltip: 'floor(시급 × 근무시간)' },
];

/**
 * 센터장/사회복지사 — 수당 필드
 * 연장/야간/휴일: 시간 입력 → 금액 계산
 * 연차: 수동 입력
 */
export const HOME_WAGE_STAFF_ALLOWANCE_FIELDS: HomeWageStaffFieldDef[] = [
  { key: 'overtimeHours', label: '연장시간', hours: true, tooltip: '연장 근무 시간' },
  { key: 'overtimePay', label: '연장수당', money: true, tooltip: 'round(시급 × 연장시간 × 1.5)' },
  { key: 'nightHours', label: '야간시간', hours: true, tooltip: '야간 근무 시간 (22:00~06:00)' },
  { key: 'nightPay', label: '야간수당', money: true, tooltip: 'round(시급 × 야간시간 × 0.5)' },
  { key: 'holidayHours', label: '휴일시간', hours: true, tooltip: '휴일 근무 시간' },
  { key: 'holidayPay', label: '휴일수당', money: true, tooltip: 'round(시급 × 휴일시간 × 1.5)' },
  { key: 'annualPay', label: '연차수당', money: true, tooltip: '연차수당 (수동 입력)' },
];

/** 센터장/사회복지사 — 합계 필드 */
export const HOME_WAGE_STAFF_TOTAL_FIELDS: HomeWageStaffFieldDef[] = [
  { key: 'totalPay', label: '총 급여', money: true, isTotal: true, tooltip: '기본급 + 연차 + 연장 + 야간 + 휴일' },
];

// ─────────────────────────────────────────────
// 가족요양 관련 상수 및 UI 요소
// ─────────────────────────────────────────────

/** 가족요양 아이콘 */
export const FAMILY_CARE_ICON = 'mdi-home-heart';

/** 가족요양 표시 색상 (보라색) */
export const FAMILY_CARE_COLOR = '#8b5cf6';

/** 가족요양 칩 스타일 */
export const FAMILY_CARE_CHIP_STYLE = {
  icon: FAMILY_CARE_ICON,
  color: FAMILY_CARE_COLOR,
  text: '가족 케어',
  textFull: '가족 케어 포함',
};

/** 가족요양 본인부담금 비율 옵션 (%) */
export const FAMILY_CARE_BONIN_RATE_OPTIONS = [
  { value: 0, label: '0%' },
  { value: 5, label: '5%' },
  { value: 10, label: '10%' },
  { value: 15, label: '15%' },
  { value: 20, label: '20%' },
] as const;

/** 가족요양 일괄 시급 적용 카테고리 */
export const FAMILY_CARE_BULK_HOURLY_CATEGORIES = [
  { key: 'family', label: '가족요양 전체', filter: (feeName: string) => feeName.includes('가족') },
  { key: 'family60', label: '가족 60분', filter: (feeName: string) => feeName.includes('가족') && feeName.includes('60분') },
  { key: 'family90', label: '가족 90분', filter: (feeName: string) => feeName.includes('가족') && feeName.includes('90분') },
] as const;

/** 가족요양 경고 메시지 */
export const FAMILY_CARE_WARNINGS = {
  under60Hours: '⚠️ 가족요양 60시간 미만: 건강보험·장기요양보험 0원, 퇴직금 미적용',
  insuranceNotApplicable: '가족요양 60시간 미만: 건강보험·장기요양보험 0원',
  retirementNotApplicable: '가족요양 60시간 미만으로 퇴직금 적립 불가',
} as const;
